import { useId } from 'react';

import TeamUserAvatar from '../Avatar/TeamUserAvatar';
import { cn } from '../cn';
import Button from '../Input2/Button';
import FilterPopover from '../Input2/FilterPopover';
import { Arrows } from '../StrokeIcons';

export type UserBreadcrumbSwitcherUser = {
  userId: string;
  userTeamName?: string | null;
  userEmail?: string | null;
  profilePicture?: string | null;
};

export type UserBreadcrumbSwitcherProps = {
  currentUser: UserBreadcrumbSwitcherUser;
  users: UserBreadcrumbSwitcherUser[];
  onUserChange: (userId: string) => void;
  className?: string;
};

function userLabel(user: UserBreadcrumbSwitcherUser) {
  return user.userTeamName ?? user.userEmail ?? 'Member';
}

export default function UserBreadcrumbSwitcher({
  currentUser,
  users,
  onUserChange,
  className,
}: UserBreadcrumbSwitcherProps) {
  const id = useId();
  const popoverId = `user-switcher-${id.replace(/:/g, '')}`;
  const anchorName = `--user-switcher-anchor-${id.replace(/:/g, '')}`;

  const hasMultipleUsers = users.length > 1;

  const userItems = users.map((u) => ({
    value: u.userId,
    label: userLabel(u),
    icon: (
      <TeamUserAvatar
        userTeam={{
          userTeamName: u.userTeamName ?? undefined,
          profilePicture: u.profilePicture ?? undefined,
          userEmail: u.userEmail ?? undefined,
        }}
        shape="circle"
        className="size-4"
      />
    ),
  }));

  return (
    <div
      className={cn(
        'flex flex-row items-center gap-1 text-nowrap text-sm [&>svg]:h-4 [&>svg]:w-4',
        'text-[#475166] dark:text-white',
        className,
      )}>
      <div className="flex items-center gap-2">
        <TeamUserAvatar
          userTeam={{
            userTeamName: currentUser.userTeamName ?? undefined,
            profilePicture: currentUser.profilePicture ?? undefined,
            userEmail: currentUser.userEmail ?? undefined,
          }}
          shape="circle"
          className="size-4"
        />
        {/* Collapse to just the avatar on mobile to keep the breadcrumb compact */}
        <span className="hidden lg:inline">{userLabel(currentUser)}</span>
      </div>
      {hasMultipleUsers && (
        <>
          <Button
            size="xs"
            text
            icon={<Arrows.ChevronDown className="size-3" />}
            tooltip="Switch user"
            popoverTarget={popoverId}
            style={{ anchorName } as React.CSSProperties}
          />
          <FilterPopover
            popoverId={popoverId}
            anchorName={anchorName}
            size="sm"
            items={userItems}
            selectedValue={currentUser.userId}
            onSelect={onUserChange}
            filterable={users.length > 5}
            popoverStyle={{ minWidth: 240 }}
          />
        </>
      )}
    </div>
  );
}
